import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../UI/Card.tsx";
import {
  OneOnOneAssignmentPdfForm,
  OneOnOneAssignmentPdfFormInputs,
} from "./OneOnOneAssignmentPdfForm.tsx";
import { useUpsertOneOnOneAssignmentPdf } from "./useUpsertOneOnOneAssignmentPdf.ts";
import { useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { useMessagePopupController } from "../UI/MessagePopup/useMessagePopupController.ts";
import { ErrorMessagePopup } from "../UI/MessagePopup/ErrorMessagePopup.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { AllProgressCheckPdfScreenPath } from "../ProgressCheckPdf/paths.ts";
import { useToAllProgressCheckPdfs } from "../ProgressCheckPdf/useToAllProgressCheckPdfs.ts";
import { useOneOnOneAssignmentPdfParamsOrThrow } from "./useOneOnOneAssignmentPdfParamsOrThrow.ts";
import { useAllOneOnOneAssignmentPdfs } from "./useAllOneOnOneAssignmentPdfs.ts";
import {
  BusinessEnglishObjective,
  OneOnOneLessonType,
  SpeakingClassLevel,
} from "../gql/graphql.ts";

export function EditOneOnOneAssignmentPdfScreen() {
  const navigate = useNavigate();
  const {
    level,
    userGroupId,
    oneOnOneLessonType,
    assignmentNumber,
    businessEnglishObjective,
  } = useOneOnOneAssignmentPdfParamsOrThrow();
  const { upsert } = useUpsertOneOnOneAssignmentPdf();
  const { pdfs } = useAllOneOnOneAssignmentPdfs();
  const current = pdfs.find(
    (p) =>
      p.level === level &&
      p.userGroupId === userGroupId &&
      p.oneOnOneLessonType === oneOnOneLessonType &&
      p.assignmentNumber === assignmentNumber &&
      (p.businessEnglishObjective ?? null) ===
        (businessEnglishObjective ?? null),
  );
  const { toAllProgressCheckPdfs } = useToAllProgressCheckPdfs();

  const { show, title, description, showPopup } = useMessagePopupController(
    4 * 1000,
  );

  async function onSave(input: OneOnOneAssignmentPdfFormInputs) {
    try {
      await upsert({
        assignmentNumber: input.assignmentNumber,
        level: input.level,
        oneOnOneLessonType: input.oneOnOneLessonType,
        pdfUrl: input.pdfUrl,
        userGroupId: input.userGroupId,
        businessEnglishObjective: input.businessEnglishObjective,
      });
    } catch (e) {
      const error = e as ApolloError;
      showPopup("Error", error?.message ?? "Unknown error");
      return;
    }

    navigate(AllProgressCheckPdfScreenPath);
  }

  return (
    <>
      <ErrorMessagePopup title={title} description={description} show={show} />
      <Layout>
        <Card>
          <ScreenTitleBlock
            title={"Edit 1on1 Assignment Pdf"}
            breadCrumbs={
              <BreadCrumbsWrapper>
                <BreadCrumbsItem
                  title={"Course PDFs"}
                  onClick={toAllProgressCheckPdfs}
                />
                <BreadCrumbsItem title={"Edit"} />
              </BreadCrumbsWrapper>
            }
          />
        </Card>
        <div className={"mt-4"}>
          <Card>
            <OneOnOneAssignmentPdfForm
              disabledMap={{
                assignmentNumber: true,
                level: true,
                oneOnOneLessonType: true,
                userGroupId: true,
                businessEnglishObjective: true,
              }}
              onSave={onSave}
              defaultValues={{
                assignmentNumber,
                level: level as unknown as SpeakingClassLevel,
                oneOnOneLessonType:
                  oneOnOneLessonType as unknown as OneOnOneLessonType,
                userGroupId,
                pdfUrl: current?.pdfUrl ?? "",
                businessEnglishObjective:
                  businessEnglishObjective as unknown as
                    | BusinessEnglishObjective
                    | undefined,
              }}
            />
          </Card>
        </div>
      </Layout>
    </>
  );
}
